import { Box, Heading, Text } from '@chakra-ui/layout'
import { IconButton, Input, Skeleton, useToast } from '@chakra-ui/react'
import { Layout } from 'components/atoms'
import { Icon } from 'components/atoms/Icon'
import { useContractContext } from 'contexts/contract'

import {
    ForwardRefRenderFunction,
    forwardRef,
    useImperativeHandle,
    useRef,
    useState
} from 'react'
import { FormFileDataProps } from './interfaces'

interface PropsFormFileData {
    hideAttachmentIcon?: boolean
    getFileData?: (file: any) => void
    clearFileData?: () => void
    allowZip?: boolean
}

const FormFileDataFC: ForwardRefRenderFunction<any, PropsFormFileData> = (
    { hideAttachmentIcon = false, getFileData, clearFileData, allowZip },
    refFileData
) => {
    const { fileData, setFileData } = useContractContext()
    const toast = useToast()

    useImperativeHandle(refFileData, () => ({
        removeFile() {
            setFileData({} as FormFileDataProps)
            clearFileData && clearFileData()
        }
    }))

    const ref = useRef<HTMLInputElement>(null)
    const handleClick = () => {
        ref.current?.click()
    }

    const [loadingData, setLoadingData] = useState(false)

    const setFileLoteImagens = (data: File) => {
        setFileData({
            anexo: {
                nome: data.name,
                base64: 'código apenas para POC'
            }
        })

        getFileData &&
            getFileData({
                anexo: {
                    nome: data.name,
                    base64: 'código apenas para POC'
                }
            })

        setLoadingData(false)
    }

    const convertToBase64 = (data: File) => {
        if (allowZip) {
            setFileLoteImagens(data)
        }

        const reader = new FileReader()

        reader.readAsDataURL(data)

        reader.onload = () => {
            const base64 =
                reader.result &&
                reader.result.toString().split(',', 2)[1]?.length / 3

            const sizeInBytes =
                (4 * Math.ceil(base64 as number) * 0.5624896334383812) / 1000000
            const sizeInKb = parseFloat(sizeInBytes.toFixed(3))

            if (sizeInKb > 5) {
                toast({
                    title: 'Sentimos muito!',
                    description:
                        'O arquivo deve possuir tamanho máximo de 5 Mb (cinco megabytes), nos formatos JPG ou PDF.',
                    position: 'bottom-left',
                    status: 'error',
                    isClosable: true
                })
                setLoadingData(false)
            } else {
                setFileData({
                    anexo: {
                        base64:
                            reader.result &&
                            reader.result.toString().split(',', 2)[1],
                        nome: data.name
                    }
                })
                getFileData &&
                    getFileData({
                        anexo: {
                            base64:
                                reader.result &&
                                reader.result.toString().split(',', 2)[1],
                            nome: data.name
                        }
                    })

                setLoadingData(false)
            }
        }
    }

    const formatIsValid = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files && event.target.files[0]
        const fileName = file && file.name
        const acceptedFormats = []

        if (allowZip) {
            acceptedFormats.push('zip')
        } else {
            acceptedFormats.push('jpg', 'pdf')
        }

        const fileExt =
            fileName && fileName.substring(fileName.lastIndexOf('.') + 1)

        return fileExt && acceptedFormats.indexOf(fileExt) !== -1
    }

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (formatIsValid(event)) {
            setLoadingData(true)
            const fileObj = event.target.files && event.target.files[0]

            event.target.value = ''

            if (fileObj && fileObj.size / 1024 <= 5120) {
                convertToBase64(fileObj)
            } else {
                setLoadingData(false)
                toast({
                    title: 'Sentimos muito!',
                    description:
                        'O arquivo deve possuir tamanho máximo de 5 Mb (cinco megabytes), nos formatos JPG ou PDF.',
                    position: 'bottom-left',
                    status: 'error',
                    isClosable: true
                })
            }
        } else {
            toast({
                title: 'Sentimos muito!',
                description: 'Tipo de arquivo não permitido',
                position: 'bottom-left',
                status: 'error',
                isClosable: true
            })
        }
    }

    const removeFile = () => {
        setFileData({} as FormFileDataProps)
        clearFileData && clearFileData()
    }

    return (
        <Layout
            type="container"
            full
            display="grid"
            gap={8}
            data-testid="form-container"
        >
            <Layout type="row" data-testid="form-heading">
                <Heading>
                    <Box position="relative">
                        <Heading
                            id="title-attachment"
                            display="flex"
                            size="md"
                            alignItems="center"
                            justifyContent={{
                                base: 'space-between',
                                sm: 'center'
                            }}
                            minH="32px"
                        >
                            Anexos
                            <IconButton
                                data-testid="input-file-icon"
                                position={{ sm: 'absolute' }}
                                right="3rem"
                                colorScheme="primary"
                                aria-label="Search database"
                                icon={<Icon name="add" />}
                                onClick={handleClick}
                            />
                            <Input
                                data-testid="input-file"
                                ref={ref}
                                type="file"
                                display={'none'}
                                accept=".jpg,.pdf"
                                onChange={handleFileChange}
                            />
                        </Heading>
                    </Box>
                </Heading>
            </Layout>

            <Box
                textAlign="center"
                display="flex"
                alignItems="center"
                justifyContent="flex-start"
                flexDirection="column"
                height="300px"
                id="attachment-box-main"
            >
                {!fileData?.anexo ? (
                    <Box
                        textAlign="center"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        flexDirection="column"
                        gap={2}
                        height="300px"
                        id="attachment-box"
                    >
                        <Box
                            textAlign="center"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            flexDirection="column"
                            gap={2}
                            zIndex={10}
                            onClick={handleClick}
                            cursor="pointer"
                        >
                            <Text
                                whiteSpace="pre"
                                color="black"
                                fontWeight="700"
                                mb="-8px"
                            >
                                Nenhum anexo selecionado.
                            </Text>
                            <Text
                                whiteSpace="pre"
                                color="black"
                                fontWeight="700"
                                textDecoration="underline"
                            >
                                Faça upload agora mesmo.
                            </Text>
                            <Text
                                whiteSpace="pre"
                                color="black"
                                maxWidth="292px"
                                fontSize={12}
                            >
                                O arquivo deve possuir tamanho máximo de 5 Mb{' '}
                                <br /> (cinco megabytes), no(s) formato(s){' '}
                                {allowZip ? '.zip' : ' JPG ou PDF'}
                            </Text>
                        </Box>
                        {!hideAttachmentIcon && (
                            <Box
                                position="absolute"
                                onClick={handleClick}
                                cursor="pointer"
                            >
                                <Icon name="file-alt-white" size={124} />
                            </Box>
                        )}
                    </Box>
                ) : (
                    <>
                        {loadingData ? (
                            <Skeleton width="100%" height="52px" />
                        ) : (
                            <Box
                                id="attachment-list"
                                bg="grayscale.50"
                                width="100%"
                                height="52px"
                                position="absolute"
                                display="flex"
                                alignItems="center"
                                flexDirection="row"
                                justifyContent="space-between"
                                padding="0 30px"
                                top="175px"
                            >
                                <Text
                                    whiteSpace="pre"
                                    color="grayscale.800"
                                    fontSize={14}
                                >
                                    {fileData?.anexo.nome}
                                </Text>
                                <Icon
                                    data-testid="icon-remove-file"
                                    name="delet"
                                    onClick={removeFile}
                                    cursor="pointer"
                                />
                            </Box>
                        )}
                    </>
                )}
            </Box>
            <Layout
                type="row"
                gutter={{ y: 1.5 }}
                data-testid="form-fields"
            ></Layout>
        </Layout>
    )
}

export const FormFileData = forwardRef(FormFileDataFC)
